import "./site/error-beacon";
import { Lattice, LatticeRotation, BoardSolve, LatticeKind, BoardRecap } from "./games/lattice/lattice";
import { recordRun, showXpToast } from "./site/profile";
import { enqueueFailedSubmit, flushFailedSubmits, isRetryable } from "./site/offline-queue";
import "./ui/styles.css";

const PB_KEY = "aether.lattice_pb";
const HANDLE_KEY = "aether.handle";

// Retry any submits that failed on a prior page load (transient 5xx / network).
flushFailedSubmits("lattice");

interface StartResp { run_id: string; seed: number; token: string; game: string }

async function startLatticeRun(): Promise<StartResp | null> {
  try {
    const res = await fetch("/api/runs/lattice", {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({}),
    });
    if (!res.ok) return null;
    return (await res.json()) as StartResp;
  } catch { return null; }
}

async function finishLatticeRun(runId: string, handle: string, rotations: LatticeRotation[], solves: BoardSolve[], kind: LatticeKind) {
  const url = `/api/runs/lattice/${runId}`;
  const body = { handle, rotations, solves, kind };
  try {
    const res = await fetch(url, {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      if (isRetryable(res.status)) enqueueFailedSubmit("lattice", url, body);
      return null;
    }
    return await res.json();
  } catch {
    enqueueFailedSubmit("lattice", url, body);
    return null;
  }
}

/** v5(a) shipped opt-in hex via `?hex=1`. v5.1 flips the default: hex
 *  is now the live topology; `?square=1` falls back to the legacy
 *  4-neighbor grid. Leaderboard rows are scoped by `kind`, so the
 *  pre-v5.1 square scores stay queryable as the square subset. */
function readKindFromUrl(): LatticeKind {
  try {
    const params = new URL(window.location.href).searchParams;
    if (params.get("square") === "1" || params.get("square") === "true") return "square";
    // Legacy ?hex=1 still works as a no-op affirmative.
    return "hex";
  } catch {
    return "hex";
  }
}

const canvas = document.getElementById("stage") as HTMLCanvasElement;
const hud = document.querySelector(".hud") as HTMLElement;
const hudBoard = document.getElementById("hud-board")!;
const hudRotations = document.getElementById("hud-rotations")!;
const hudScore = document.getElementById("hud-score")!;
const msgTitle = document.getElementById("msg-title")!;
const msgBody = document.getElementById("msg-body")!;
const btnStart = document.getElementById("btn-start") as HTMLButtonElement;
const submitForm = document.getElementById("submit-form") as HTMLFormElement;
const handleInputOverlay = document.getElementById("handle-input-overlay") as HTMLInputElement;
const boardRecapEl = document.getElementById("board-recap") as HTMLElement | null;
const announcer = document.getElementById("lattice-announcer") as HTMLElement | null;
function announce(msg: string) {
  if (!announcer) return;
  announcer.textContent = "";
  setTimeout(() => { if (announcer) announcer.textContent = msg; }, 30);
}
function prefersReducedMotion(): boolean {
  try { return matchMedia("(prefers-reduced-motion: reduce)").matches; }
  catch { return false; }
}

// v5(c): render per-board recap on game-over. Each row shows "Board N:
// rotations/par" with badges for locked/multi-color boards and a colored
// dot indicating under/at/over par.
function renderBoardRecap(recap: BoardRecap[]) {
  if (!boardRecapEl) return;
  if (recap.length === 0) {
    boardRecapEl.hidden = true;
    boardRecapEl.innerHTML = "";
    return;
  }
  const rows = recap.map((r) => {
    const delta = r.par - r.rotations;
    const tone = delta > 0 ? "under" : delta === 0 ? "even" : "over";
    const badges: string[] = [];
    if (r.locked) badges.push('<span class="br-badge locked">🔒</span>');
    if (r.multi_color) badges.push('<span class="br-badge rainbow">🌈</span>');
    return `<li class="br-row ${tone}"><span class="br-idx">Board ${r.board_idx + 1}</span><span class="br-moves">${r.rotations}/${r.par}</span>${badges.join("")}</li>`;
  });
  boardRecapEl.innerHTML = `<ul class="br-list">${rows.join("")}</ul>`;
  boardRecapEl.hidden = false;
}

let game: Lattice | null = null;
let activeRunId: string | null = null;
let pendingPayload: { rotations: LatticeRotation[]; solves: BoardSolve[]; kind: LatticeKind } | null = null;

import { getDailySeed, markDailyComplete, isDailyRun } from "./site/daily";

async function startRun() {
  if (game) game.dispose();
  const dseed = getDailySeed();
  const runStart = dseed === null ? await startLatticeRun() : null;
  activeRunId = runStart?.run_id ?? null;
  const seed = dseed ?? runStart?.seed ?? Math.floor(Math.random() * 0x7fffffff);

  const kind = readKindFromUrl();
  const reducedMotion = prefersReducedMotion();
  game = new Lattice(canvas, {
    onAnnounce: announce,
    onHud: (boardIdx, rotations, scoreEstimate) => {
      hudBoard.textContent = String(boardIdx);
      hudRotations.textContent = String(rotations);
      hudScore.textContent = scoreEstimate.toLocaleString();
    },
    onGameOver: async ({ rotations, solves, scoreEstimate, kind: playedKind, boardRecap }) => {
      hud.dataset.state = "over";
      msgTitle.textContent = "Run complete";
      msgBody.textContent = `${scoreEstimate.toLocaleString()} pts · ${solves.length} boards solved · ${rotations.length} rotations`;
      announce(`Run complete. ${solves.length} boards solved. ${scoreEstimate.toLocaleString()} points.`);
      renderBoardRecap(boardRecap);
      btnStart.textContent = "Try again";
      try {
        const pb = parseInt(localStorage.getItem(PB_KEY) || "0", 10) || 0;
        if (scoreEstimate > pb) localStorage.setItem(PB_KEY, String(scoreEstimate));
      } catch { /* private */ }
      const xp = recordRun({ game: "lattice", score: scoreEstimate });
      showXpToast(xp.earnedXp, xp.totalXp, xp.rank);
      if (isDailyRun()) markDailyComplete("lattice", scoreEstimate);

      if (!activeRunId) return;
      const handle = localStorage.getItem(HANDLE_KEY) ?? "";
      if (handle) {
        const res = await finishLatticeRun(activeRunId, handle, rotations, solves, playedKind);
        if (res) msgBody.textContent += ` · ranked #${res.rank} of ${res.total}`;
      } else {
        pendingPayload = { rotations, solves, kind: playedKind };
        submitForm.hidden = false;
      }
    },
  }, seed, kind, { reducedMotion });
  game.armAudio();
  try {
    const raw = localStorage.getItem("aether.settings");
    if (raw) {
      const s = JSON.parse(raw);
      if (typeof s.audioVolume === "number") game.setVolume(s.audioVolume);
      if (typeof s.muted === "boolean") game.setMuted(s.muted);
    }
  } catch { /* defaults */ }
  game.start();
  hud.dataset.state = "running";
  if (dseed !== null) {
    const daily = new URL(window.location.href).searchParams.get("daily");
    msgTitle.textContent = `Lattice · daily ${daily}`;
  } else {
    msgTitle.textContent = kind === "square" ? "Lattice · square (legacy)" : "Lattice";
  }
  msgBody.textContent = "";
}

btnStart.addEventListener("click", startRun);

submitForm.addEventListener("submit", async (e) => {
  e.preventDefault();
  if (!activeRunId || !pendingPayload) return;
  const value = handleInputOverlay.value.trim();
  if (!/^[A-Za-z0-9_-]{1,16}$/.test(value)) return;
  localStorage.setItem(HANDLE_KEY, value);
  const res = await finishLatticeRun(activeRunId, value, pendingPayload.rotations, pendingPayload.solves, pendingPayload.kind);
  pendingPayload = null;
  submitForm.hidden = true;
  if (res) msgBody.textContent += ` · ranked #${res.rank} of ${res.total}`;
});

window.addEventListener("keydown", (e) => {
  if (e.target instanceof HTMLInputElement) return;
  if (e.code === "Enter" && hud.dataset.state !== "running") startRun();
});
